import { ThreadPrimitive } from "@assistant-ui/react";
import { Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserValue } from "../user";
import { getGreetingKey, markGreetingShown, shouldShowGreetingToday } from "./dailyGreeting";

/**
 * Empty new-chat screen. Vertically it fills the thread's canvas so the two zones pin to opposite
 * ends:
 *
 * 1. top — a time-of-day greeting (panel-heading style; first open of the day only);
 * 2. bottom — a "Try asking" list of example prompts, anchored just above the composer (which the
 *    shared Thread keeps pinned below this component).
 *
 * The big illustration and the "How can I help you?" line were removed: the invitation now lives in
 * the composer placeholder (see SphereAIThread). Suggestion rows use `ThreadPrimitive.Suggestion` to
 * fill (not send) the composer, so prompts can be edited before sending.
 */

/**
 * The Suggestion primitive fills the composer but doesn't move focus there, so the user would
 * have to click the textarea before editing. Focus it ourselves (mirrors the library's own
 * suggestion chips): with `asChild`, Radix Slot runs the fill handler before this onClick, and the
 * rAF defers past React's re-render so the caret lands at the end of the inserted prompt.
 */
function focusComposer(event: React.MouseEvent<HTMLButtonElement>): void {
	const input = event.currentTarget
		.closest(".aui-thread-root")
		?.querySelector<HTMLTextAreaElement>(".aui-composer-input");
	if (!input) return;
	requestAnimationFrame(() => {
		input.focus({ preventScroll: true });
		input.setSelectionRange(input.value.length, input.value.length);
	});
}

/**
 * Resolves the once-per-day welcome greeting for this mount.
 *
 * Returns the localized, first-name-personalized greeting on the **first panel open of the local
 * calendar day**, and `null` on every later open the same day. Reading `show` during render but
 * persisting it in an effect keeps this correct under React StrictMode's double-invoked render —
 * the greeting is never marked "shown" unless it actually renders.
 */
function useDailyGreeting(): string | null {
	const { t } = useTranslation();
	const { name } = useUserValue();
	// One moment per mount, so the time-of-day band and the "today" comparison agree.
	const [now] = useState(() => new Date());
	const [show] = useState(() => shouldShowGreetingToday(now));
	useEffect(() => {
		if (show) {
			markGreetingShown(now);
		}
	}, [show, now]);
	return show ? t(getGreetingKey(now), { name: name ?? "" }) : null;
}

export function SphereAIWelcome(): React.JSX.Element {
	const { t } = useTranslation();
	const greeting = useDailyGreeting();

	const suggestions = t("sphereAi.suggestions", { returnObjects: true });

	return (
		/* In the reading lane, like everything else in the panel (see SphereAIPanelLane): the greeting
		   and the suggestion rows sit directly above the composer, which is centered at that same width,
		   so a full-width list here would have run out past both ends of the input it introduces. The
		   cap is inert at the docked width. `flex-col` and `h-full` stay on this element — `mt-auto` is
		   what anchors the suggestions to the bottom. */
		<div className="mx-auto flex h-full w-full max-w-(--thread-max-width) flex-col">
			{/* Top zone — greeting. */}
			<div className="flex flex-col gap-4 pt-2">
				{greeting && (
					<Text as="div" type="Body/L/Bold" color={themeCSSVars.palette_N700} className="px-2">
						{greeting}
					</Text>
				)}
			</div>

			{/* Bottom zone — "Try asking", anchored above the composer (mt-auto pushes it down). `pb-1`
			    keeps the last suggestion's rule off the composer's own border, which otherwise meet. */}
			<div className="mt-auto w-full pt-6 pb-1">
				<Text as="div" type="Body/S/Medium" color={themeCSSVars.palette_N400} className="mb-2 px-2">
					{t("sphereAi.tryAsking")}
				</Text>
				<div className="divide-y divide-[var(--palette-N100)] border-y border-[var(--palette-N100)]">
					{suggestions.map((prompt) => (
						<ThreadPrimitive.Suggestion key={prompt} prompt={prompt} send={false} asChild>
							<button
								type="button"
								onClick={focusComposer}
								className="flex w-full px-2 py-2 text-left transition-colors hover:bg-[var(--palette-N100)]"
							>
								<Text as="span" type="Body/M/Book" color={themeCSSVars.palette_N700}>
									{prompt}
								</Text>
							</button>
						</ThreadPrimitive.Suggestion>
					))}
				</div>
			</div>
		</div>
	);
}
